import { NewBillableActivityTypeObject } from '../components/contract/package/custom-package-types';
import {
  billableActivitiesMap,
  billableActivitiesMapv2,
} from '../constants/strings/contract/form-constants';
import {
  BillableActivityType,
  EngagementType,
  PackageOfferingsType,
  ProgramIndicationType,
} from '../types';
import {
  capitalizeFirstLetterAndSpaceCamelCaseString,
  capitalizeFirstLetterAndSpaceSnakeString,
} from './bills-utils';

export const NOT_FOR_DISPLAY = new Set([
  'unified_v1_multi_shim',
  'prevention_whole_body',
]);

export const READ_ONLY_PROGRAM_NAMES = new Set(['expert_medical_opinion']);

export const isReadOnlyOffering = (pi: ProgramIndicationType): boolean =>
  READ_ONLY_PROGRAM_NAMES.has(pi.programName);

export interface IndicationCategory {
  name: string;
  items: ProgramIndicationType[];
  getDisplayLabel: (pi: ProgramIndicationType) => string;
}

export const categorizeIndicationsForForm = (
  indications: ProgramIndicationType[],
): IndicationCategory[] => {
  const visible = indications.filter(pi => !NOT_FOR_DISPLAY.has(pi.identifier));

  const groups: Record<string, ProgramIndicationType[]> = {};
  const other: ProgramIndicationType[] = [];

  for (const pi of visible) {
    const category = getCategoryName(pi);
    if (category) {
      groups[category] = groups[category] ?? [];
      groups[category].push(pi);
    } else {
      other.push(pi);
    }
  }

  const result: IndicationCategory[] = Object.keys(groups)
    .sort((a, b) => a.localeCompare(b))
    .map(name => ({
      name,
      items: groups[name].sort((a, b) =>
        a.indicationName.localeCompare(b.indicationName),
      ),
      getDisplayLabel:
        name === 'Pelvic Health'
          ? getPelvicDisplayLabel
          : getDefaultDisplayLabel,
    }));

  if (other.length > 0) {
    result.push({
      name: 'Other',
      items: other.sort((a, b) => a.identifier.localeCompare(b.identifier)),
      getDisplayLabel: getOtherDisplayLabel,
    });
  }

  return result;
};

const getCategoryName = (pi: ProgramIndicationType): string | null => {
  if (
    pi.programName === 'chronic' &&
    (pi.indicationName === 'pelvic' || pi.indicationName.startsWith('pelvic_'))
  )
    return 'Pelvic Health';
  if (pi.programName === 'chronic' && pi.indicationName === 'balance')
    return 'Balance';
  if (pi.programName === 'migraine') return 'Migraine';
  if (pi.programName === 'chronic') return 'Chronic';
  if (pi.programName === 'acute') return 'Acute';
  if (pi.programName === 'perisurgical') return 'Perisurgical';
  return null;
};

const getDefaultDisplayLabel = (pi: ProgramIndicationType): string =>
  capitalizeFirstLetterAndSpaceSnakeString(pi.indicationName);

const getPelvicDisplayLabel = (pi: ProgramIndicationType): string =>
  capitalizeFirstLetterAndSpaceSnakeString(
    pi.indicationName.replace(/^pelvic_/, ''),
  );

const getOtherDisplayLabel = (pi: ProgramIndicationType): string =>
  capitalizeFirstLetterAndSpaceSnakeString(pi.identifier);

export const isBillableActivitiesWithDetails = (
  bat: BillableActivityType[],
): boolean =>
  bat.some(activity => activity.details && activity.details !== null);

export const isBillableActivityType = (
  bat: number[] | BillableActivityType[],
): bat is BillableActivityType[] =>
  (bat as BillableActivityType[]).every(
    (ba: BillableActivityType) => ba.id !== undefined,
  );

export const normalizeBillableActivityTypesForComparison = (
  bat: number[] | NewBillableActivityTypeObject | BillableActivityType[],
): NewBillableActivityTypeObject | number[] => {
  if (!Array.isArray(bat)) {
    return bat;
  }

  if (!isBillableActivityType(bat)) {
    return bat.toSorted((a, b) => a - b);
  }

  if (isBillableActivitiesWithDetails(bat)) {
    return bat.reduce((acc: NewBillableActivityTypeObject, activity) => {
      acc[activity.engagementTypeId] = activity.details;
      return acc;
    }, {});
  }
  return bat.map(activity => activity.engagementTypeId).sort((a, b) => a - b);
};

/**
 * @param programIndications
 *split into x number of arrays based on first word in indication,
 *return object of arrays:
 * @returns Record<string, PackageOfferingType>[]
 */
export const categorizePackageIndications = (
  packageOfferings: PackageOfferingsType[],
): Record<string, PackageOfferingsType[]> =>
  packageOfferings.reduce<Record<string, PackageOfferingsType[]>>(
    (acc, curr) => {
      const splitPi = curr.programIndication?.split('_') ?? [];
      if (splitPi.length === 0) {
        return acc;
      }

      const program = splitPi[0];
      return {
        ...acc,
        [program]: [...(acc[program] ?? []), curr],
      };
    },
    {},
  );

/**
 * @param indicationList
 * i.e [{programIndication: 'chronic_pelvic_pain'}, {programIndication: 'chronic_back'}]
 * @returns string
 * i.e 'Pelvic Pain, Back'
 **/
export const listifyIndications = (
  indicationList: Pick<PackageOfferingsType, 'programIndication'>[],
): string =>
  indicationList
    .map(val => {
      const segments = val.programIndication?.split('_') ?? [];
      const indication =
        segments.length <= 1 ? segments.join('') : segments.slice(1).join('_');

      return capitalizeFirstLetterAndSpaceSnakeString(indication);
    })
    .join(', ');

/**
 *
 * @param ba string | BillableActivityType | EngagementType
 * @returns string
 * take in a string 'enso_session' or {id: 1, name: 'enso_session', details: {dailyCap: 1}}
 * returns 'Enso session' or 'Enso session: Daily Cap 1'
 */
export const getBillableActivityDisplayString = (
  ba: string | BillableActivityType | EngagementType,
): string => {
  if (typeof ba !== 'string') {
    if ('details' in ba && ba.details) {
      const activityName = billableActivitiesMap[ba.name];
      const detail = ba.details
        ? `: ${capitalizeFirstLetterAndSpaceCamelCaseString(Object.keys(ba.details)[0])} ${ba.details[Object.keys(ba.details)[0]]}`
        : '';
      return `${activityName}${detail}`;
    } else {
      return billableActivitiesMapv2[ba.name];
    }
  }
  return billableActivitiesMapv2[ba];
};
